import { atom, useSetAtom } from "jotai";
import { useMemo } from "react";

import type { AddressTextField } from "../types";
import type { AddressCity, AddressSuggestion } from "../types";

/**
 * The address fields a form holds, whatever else it holds beside them. Only
 * the street is required: a caller that has no canton field simply leaves it
 * out, and nothing is written there.
 */
export type AddressFieldGroup = {
  street: AddressTextField;
  postalCode?: AddressTextField;
  city?: AddressTextField;
  state?: AddressTextField;
  country?: AddressTextField;
};

export type AddressAutofillOptions = {
  /**
   * Whether picking a commune or a street rewrites the postal code. A form
   * that lets the code be typed by hand turns this off.
   */
  postalCode?: boolean;
  /** Whether picking a commune or a street rewrites the canton. */
  state?: boolean;
};

/** Stands in for a field the form does not hold; writing to it does nothing. */
const NOWHERE = atom<string | null | undefined>("");

type Setter = (value: string) => void;

export type AddressAutofill = {
  onCitySelected: (city: AddressCity) => void;
  onCityCleared: () => void;
  onAddressSelected: (address: AddressSuggestion) => void;
};

/**
 * What picking a suggestion fills in beside it. A commune carries its postal
 * code, its canton and its country; a street carries its commune too.
 * Everything the form does not hold is skipped.
 */
export function useAddressAutofill(
  fields: AddressFieldGroup,
  options: AddressAutofillOptions = {},
): AddressAutofill {
  const { postalCode: fillPostalCode = true, state: fillState = true } =
    options;

  const setPostalCode = useSetAtom(
    fields.postalCode?.atom ?? NOWHERE,
  ) as Setter;

  const setCity = useSetAtom(fields.city?.atom ?? NOWHERE) as Setter;
  const setState = useSetAtom(fields.state?.atom ?? NOWHERE) as Setter;
  const setCountry = useSetAtom(fields.country?.atom ?? NOWHERE) as Setter;

  const hasPostalCode = undefined !== fields.postalCode;
  const hasCity = undefined !== fields.city;
  const hasState = undefined !== fields.state;
  const hasCountry = undefined !== fields.country;

  return useMemo(
    () => ({
      onCitySelected: (city) => {
        if (fillPostalCode && hasPostalCode) {
          setPostalCode(city.postalCode ?? "");
        }

        if (fillState && hasState && city.stateCode) {
          setState(city.stateCode);
        }

        if (hasCountry && city.countryIso2) {
          setCountry(city.countryIso2);
        }
      },

      onCityCleared: () => {
        if (fillPostalCode && hasPostalCode) {
          setPostalCode("");
        }
      },

      onAddressSelected: (address) => {
        if (fillPostalCode && hasPostalCode) {
          setPostalCode(address.postalCode ?? "");
        }

        if (hasCity) {
          setCity(address.cityName);
        }

        if (fillState && hasState && address.canton) {
          setState(address.canton);
        }

        if (hasCountry && address.countryIso2) {
          setCountry(address.countryIso2);
        }
      },
    }),
    [
      fillPostalCode,
      fillState,
      hasPostalCode,
      hasCity,
      hasState,
      hasCountry,
      setPostalCode,
      setCity,
      setState,
      setCountry,
    ],
  );
}
